/**
 * useTreeMapSurface Hook
 *
 * Handles data loading for TreeMap using the context tree execution API.
 * Initial load fetches the tree structure in one request, then flattens into columns.
 * Pagination uses the context children endpoint with token-based continuation.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import type { ApiClient } from '@center-technologies/shared-api-clients';
import type { UnifiedFilters } from 'types/filters';
import type { Context, RuntimeFilterEnvelope, SortOrder, SphereNode } from 'types/context';
import type {
  Sphere,
  BasicSphereInfo,
} from './types';
import { expandContextLevels } from './defaults';
import { useSpherePolling } from '../../../hooks/useSpherePolling';
import { executeContextTree, loadContextChildren } from '../../../api/contextTreeApi';

export type { BasicSphereInfo };

export interface TreeMapSurfaceData {
  // Spheres organized by column level
  spheresByColumn: Record<number, Sphere[]>;

  // Pagination state per parent sphere
  paginationState: Record<string, {
    nextToken?: string;
    hasMore: boolean;
    loading: boolean;
  }>;

  // Lookup map for sphere basic info (used for displaying assignees)
  sphereLookup: Record<string, BasicSphereInfo>;

  // Overall loading state
  loading: boolean;
  /**
   * True once a tree fetch has succeeded. Separates the first load (nothing to
   * show yet) from a reload: an empty result is still a loaded board, so a
   * filter reload over it must not be treated as a first load.
   */
  hasLoaded: boolean;
  error: string | null;
}

export interface UseTreeMapSurfaceParams {
  context: Context | null;
  rootSphereId: string;
  organizationId: string;
  apiClient: ApiClient | null;
  defaultPageSize?: number;
  cardView?: 'minimal' | 'standard' | 'expanded';  // Detail level for sphere data
  /** Per-level runtime filters (EAINT-781, `useLevelFilters`) — applied on every tree fetch below. */
  runtimeFilters?: RuntimeFilterEnvelope;
}

export interface UseTreeMapSurfaceReturn extends TreeMapSurfaceData {
  // Load all hierarchy data using context tree execution (one request for all levels)
  loadInitialData: () => Promise<void>;

  // Re-execute the context tree query, honouring the current `runtimeFilters`
  reloadWithFilters: () => Promise<void>;

  // Load more children for a specific parent (pagination using continuation token)
  // Returns the loaded spheres
  loadChildren: (
    parentSphereId: string,
    level: number,
    options?: {
      token?: string;
      mode?: 'append' | 'overwrite';
      customFilters?: UnifiedFilters;
      customSortOrder?: SortOrder[];
    }
  ) => Promise<Sphere[]>;

  // Refresh data for a specific level
  refreshLevel: (level: number) => Promise<void>;

  // Update a specific sphere in the local state (without API call)
  updateSphereInState: (updatedSphere: Sphere) => void;

  // Add a new sphere to the local state (without API call)
  addSphereToState: (newSphere: Sphere, parentId: string) => void;

  // Load basic info for assignee spheres (batch request)
  loadAssigneeInfo: (sphereIds: string[]) => Promise<void>;

  // Clear all data
  clearData: () => void;

  // Poll for sphere updates after mutations (to catch status cascades, flow updates, etc.)
  pollForUpdates: (
    sphereIds: string[],
    alwaysPollIds?: string[],
    options?: { maxAttempts?: number; intervalMs?: number }
  ) => void;

  // Re-read spheres once for a change already stored (a glyph, colour or picture); a failed read keeps the node
  refreshSpheres: (sphereIds: string[]) => Promise<void>;
}

/**
 * Convert a SphereNode from the context tree API to the TreeMap Sphere format.
 */
function sphereNodeToSphere(node: SphereNode, parentId?: string): Sphere {
  // Convert assignedPerspectives array to perspectives Record
  const perspectives: Record<string, string[]> = {};
  if (node.assignedPerspectives) {
    for (const ap of node.assignedPerspectives) {
      const systemName = ap.perspectiveSystemName;
      if (systemName && ap.assignedSpheres) {
        const assigneeIds = ap.assignedSpheres.map(a => a.id).filter(Boolean);
        if (assigneeIds.length > 0) {
          perspectives[systemName] = assigneeIds;
        }
      }
    }
  }

  return {
    id: node.id,
    schema_type: node.schemaType || '',
    properties: node.properties as Record<string, any>,
    parent_id: parentId,
    organization_id: '', // Not available from tree API; set by consumer if needed
    label: node.label,
    glyph: node.glyph,
    glyphColor: node.glyphColor,
    perspectives: Object.keys(perspectives).length > 0 ? perspectives : undefined,
    ...(node.status ? { statusPoint: node.status } : {}),
    ...(node.flow ? { flowData: node.flow } : {}),
  };
}

/**
 * Flatten a SphereNode tree into spheresByColumn and pagination state.
 * Each level in the tree becomes a column.
 */
function flattenTreeToColumns(
  roots: SphereNode[],
  parentId?: string
): {
  spheresByColumn: Record<number, Sphere[]>;
  paginationState: Record<string, { nextToken?: string; hasMore: boolean; loading: boolean }>;
} {
  const spheresByColumn: Record<number, Sphere[]> = {};
  const paginationState: Record<string, { nextToken?: string; hasMore: boolean; loading: boolean }> = {};

  function traverse(nodes: SphereNode[], parentSphereId?: string) {
    for (const node of nodes) {
      const level = node.level;
      if (!spheresByColumn[level]) {
        spheresByColumn[level] = [];
      }
      spheresByColumn[level].push(sphereNodeToSphere(node, parentSphereId));

      // Track pagination for nodes that have childPagination
      if (node.childPagination) {
        paginationState[node.id] = {
          nextToken: node.childPagination.nextToken,
          hasMore: node.childPagination.hasMore,
          loading: false,
        };
      }

      if (node.children.length > 0) {
        traverse(node.children, node.id);
      }
    }
  }

  traverse(roots, parentId);
  return { spheresByColumn, paginationState };
}

/**
 * Hook for managing TreeMap data loading via context tree execution API
 */
export function useTreeMapSurface({
  context,
  rootSphereId,
  organizationId,
  apiClient,
  defaultPageSize = 5,
  cardView = 'standard',
  runtimeFilters
}: UseTreeMapSurfaceParams): UseTreeMapSurfaceReturn {

  const [spheresByColumn, setSpheresByColumn] = useState<Record<number, Sphere[]>>({});
  const [paginationState, setPaginationState] = useState<Record<string, { nextToken?: string; hasMore: boolean; loading: boolean }>>({});
  const [sphereLookup, setSphereLookup] = useState<Record<string, BasicSphereInfo>>({});
  const [loading, setLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Track in-flight requests to prevent duplicates
  const inflightRequests = useRef<Set<string>>(new Set());
  const isLoadingInitialDataRef = useRef(false);

  /**
   * Execute the context tree query via the shared, envelope-aware API client
   * function (POSTs when `runtimeFilters` is present, GETs otherwise — see
   * `contextTreeApi.executeContextTree`), then flatten the result into columns.
   */
  const fetchTree = useCallback(async (refreshStructure: boolean) => {
    if (!apiClient || !context || !rootSphereId) {
      setError('Missing required parameters');
      return;
    }

    const systemName = context.system_name;
    if (!systemName) {
      throw new Error('Context system_name is required for context execution');
    }

    const levels = context.definition?.levels || [];
    const expandedLevels = expandContextLevels(levels);

    const response = await executeContextTree(apiClient, systemName, {
      rootSphereId,
      limit: defaultPageSize,
      // Disable lazy loading to fetch all levels upfront for TreeMap
      lazyLoad: false,
      ...(expandedLevels.length > 0 && { depth: expandedLevels.length }),
      ...(refreshStructure && { refreshStructure: true }),
      runtimeFilters,
    });

    const { spheresByColumn: columns, paginationState: pagination } = flattenTreeToColumns(
      response.roots
    );

    setSpheresByColumn(columns);
    setPaginationState(pagination);
    setHasLoaded(true);
  }, [apiClient, context, rootSphereId, defaultPageSize, runtimeFilters]);

  /**
   * Load initial data using context tree execution endpoint
   */
  const loadInitialData = useCallback(async () => {
    // Prevent duplicate in-flight initial data requests
    if (isLoadingInitialDataRef.current) {
      return;
    }

    isLoadingInitialDataRef.current = true;
    setLoading(true);
    setError(null);

    try {
      await fetchTree(false);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load initial data';
      setError(message);
    } finally {
      setLoading(false);
      isLoadingInitialDataRef.current = false;
    }
  }, [fetchTree]);

  /**
   * Re-execute the context tree query with `refreshStructure` set, honouring
   * the hook's current `runtimeFilters` (EAINT-781 `useLevelFilters`).
   */
  const reloadWithFilters = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      await fetchTree(true);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load filtered data';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [fetchTree]);

  /**
   * Load children for a specific parent at a given level using context children endpoint.
   * Returns the loaded spheres for recursive loading.
   */
  const loadChildren = useCallback(async (
    parentSphereId: string,
    level: number,
    options: {
      token?: string;
      mode?: 'append' | 'overwrite';
      customFilters?: UnifiedFilters;
      customSortOrder?: SortOrder[];
    } = {}
  ): Promise<Sphere[]> => {
    if (!apiClient || !context) {
      return [];
    }

    const { token, mode = 'append' } = options;

    // Expand levels based on repeat property, then look up config for this level
    const levels = context.definition?.levels || [];
    const expandedLevels = expandContextLevels(levels);
    const levelConfig = expandedLevels[level] || null;

    if (!levelConfig) {
      return [];
    }

    // Prevent duplicate requests
    const requestKey = `${parentSphereId}-${level}-${token || 'initial'}`;
    if (inflightRequests.current.has(requestKey)) {
      return [];
    }

    inflightRequests.current.add(requestKey);

    // Set loading state for this parent
    setPaginationState(prev => ({
      ...prev,
      [parentSphereId]: {
        ...prev[parentSphereId],
        loading: true
      }
    }));

    try {
      // Check depth limit
      const maxDepth = context.definition?.settings?.maxDepth;
      const currentDepth = level + 1;

      if (maxDepth && currentDepth >= maxDepth) {
        return [];
      }

      const systemName = context.system_name;
      if (!systemName) {
        return [];
      }

      // Same envelope-aware client function `fetchTree` uses (POSTs when
      // `runtimeFilters` is present, GETs otherwise). Passing the envelope here
      // is load-bearing, not cosmetic: without it, expanding a node — or a
      // `refreshLevel` in 'overwrite' mode, which REPLACES the filtered page —
      // returns the unfiltered child set, so the board silently widens as the
      // user drills in (EAINT-781 spec § "expanding a node silently un-filters").
      const response = await loadContextChildren(apiClient, systemName, {
        parentSphereId,
        level,
        rootSphereId,
        limit: defaultPageSize,
        ...(token && { token }),
        ...(runtimeFilters && { runtimeFilters }),
      });

      // Convert SphereNode children to Sphere format
      const spheres = response.children.map(child =>
        sphereNodeToSphere(child, parentSphereId)
      );

      // Update spheres by column
      const targetColumn = level + 1;
      setSpheresByColumn(prev => {
        let updatedColumn;
        if (mode === 'overwrite') {
          updatedColumn = spheres;
        } else {
          // Deduplicate: remove any existing spheres with the same IDs before appending
          const existing = prev[targetColumn] || [];
          const newSphereIds = new Set(spheres.map(s => s.id));
          const filteredExisting = existing.filter(s => !newSphereIds.has(s.id));
          updatedColumn = [...filteredExisting, ...spheres];
        }

        return {
          ...prev,
          [targetColumn]: updatedColumn
        };
      });

      // Update pagination state from response
      const nextToken = response.pagination?.nextToken;
      setPaginationState(prev => ({
        ...prev,
        [parentSphereId]: {
          nextToken,
          hasMore: response.pagination?.hasMore ?? false,
          loading: false
        }
      }));

      // Also track pagination for any children that have childPagination
      for (const child of response.children) {
        if (child.childPagination) {
          setPaginationState(prev => ({
            ...prev,
            [child.id]: {
              nextToken: child.childPagination?.nextToken,
              hasMore: child.childPagination?.hasMore ?? false,
              loading: false,
            }
          }));
        }
      }

      return spheres;

    } catch {
      // Intentionally swallowed: child loading failures are non-fatal; reset loading state and return empty results
      setPaginationState(prev => ({
        ...prev,
        [parentSphereId]: {
          ...prev[parentSphereId],
          loading: false
        }
      }));
      return [];
    } finally {
      inflightRequests.current.delete(requestKey);
    }
  }, [apiClient, context, defaultPageSize, rootSphereId, runtimeFilters]);

  /**
   * Refresh all data for a specific level
   */
  const refreshLevel = useCallback(async (level: number) => {
    const levels = context?.definition?.levels;
    if (!levels || level >= levels.length) {
      return;
    }

    // Get all parent spheres from previous level
    const parentLevel = level === 0 ? 0 : level;
    const parentSpheres = spheresByColumn[parentLevel] || [];

    // Reload children for each parent
    for (const parent of parentSpheres) {
      await loadChildren(parent.id, level, { mode: 'overwrite' });
    }
  }, [context, spheresByColumn, loadChildren]);

  /**
   * Convert assignedPerspectives array format to perspectives Record format.
   * API returns assignedPerspectives: [{ perspectiveProperties: {system_name}, assigned: [{sphere: {id}}] }]
   * TreeMapCard expects perspectives: { system_name: [assigneeId1, assigneeId2] }
   */
  const convertAssignedPerspectivesToRecord = (assignedPerspectives: any[] | undefined): Record<string, string[]> | undefined => {
    if (!assignedPerspectives || !Array.isArray(assignedPerspectives)) {
      return undefined;
    }

    const perspectivesRecord: Record<string, string[]> = {};

    for (const ap of assignedPerspectives) {
      const systemName = ap.perspectiveProperties?.system_name;
      if (!systemName) continue;

      const assigneeIds = ap.assigned?.map((a: any) => a.sphere?.id).filter(Boolean) || [];
      if (assigneeIds.length > 0) {
        perspectivesRecord[systemName] = assigneeIds;
      }
    }

    return Object.keys(perspectivesRecord).length > 0 ? perspectivesRecord : undefined;
  };

  /**
   * Update a specific sphere in the local state (without API call)
   */
  const updateSphereInState = useCallback((updatedSphere: Sphere) => {
    setSpheresByColumn(prev => {
      const updatedColumns = { ...prev };
      let found = false;

      for (const [column, spheres] of Object.entries(updatedColumns)) {
        const index = spheres.findIndex(s => s.id === updatedSphere.id);
        if (index !== -1) {
          const existingSphere = spheres[index];

          // Convert assignedPerspectives to perspectives Record format if present
          const updatedSphereAny = updatedSphere as any;
          const convertedPerspectives = convertAssignedPerspectivesToRecord(updatedSphereAny.assignedPerspectives);

          // Merge updated properties with existing sphere to preserve critical TreeMap properties
          const mergedSphere = {
            ...existingSphere,
            ...updatedSphere,
            parent_id: updatedSphere.parent_id ?? existingSphere.parent_id,
            label: updatedSphere.label ?? existingSphere.label,
            glyph: updatedSphere.glyph ?? existingSphere.glyph,
            glyphColor: updatedSphere.glyphColor ?? existingSphere.glyphColor,
            properties: {
              ...existingSphere.properties,
              ...updatedSphere.properties,
            },
            perspectives: convertedPerspectives || updatedSphere.perspectives || existingSphere.perspectives,
          };

          updatedColumns[Number.parseInt(column)] = [
            ...spheres.slice(0, index),
            mergedSphere,
            ...spheres.slice(index + 1)
          ];
          found = true;
          break;
        }
      }

      return updatedColumns;
    });
  }, []);

  /**
   * Add a new sphere to the local state (without API call)
   */
  const addSphereToState = useCallback((newSphere: Sphere, parentId: string) => {
    setSpheresByColumn(prev => {
      const updatedColumns = { ...prev };
      let targetColumn: number | null = null;

      // Find parent's column
      for (const [column, spheres] of Object.entries(updatedColumns)) {
        if (spheres.some(s => s.id === parentId)) {
          targetColumn = Number.parseInt(column);
          break;
        }
      }

      if (targetColumn === null) {
        return updatedColumns;
      }

      if (!updatedColumns[targetColumn]) {
        updatedColumns[targetColumn] = [];
      }
      updatedColumns[targetColumn] = [...updatedColumns[targetColumn], newSphere];

      return updatedColumns;
    });
  }, []);

  /**
   * Load basic info for assignee spheres (batch request)
   */
  const loadAssigneeInfo = useCallback(async (sphereIds: string[]) => {
    if (sphereIds.length === 0) {
      return;
    }

    const uniqueIds = [...new Set(sphereIds)];
    const newLookupEntries: Record<string, BasicSphereInfo> = {};
    const missingIds: string[] = [];

    for (const id of uniqueIds) {
      if (sphereLookup[id]) {
        continue;
      }

      let foundInColumns = false;
      for (const spheres of Object.values(spheresByColumn)) {
        const sphere = spheres.find(s => s.id === id);
        if (sphere) {
          newLookupEntries[id] = {
            id: sphere.id,
            label: sphere.label,
            name: sphere.properties?.name || '',
            type: sphere.properties?.type || '',
            glyph: sphere.glyph || '',
            glyphColor: sphere.glyphColor,
            imageId: sphere.properties?.imageId || ''
          };
          foundInColumns = true;
          break;
        }
      }

      if (!foundInColumns) {
        missingIds.push(id);
      }
    }

    if (Object.keys(newLookupEntries).length > 0) {
      setSphereLookup(prev => ({
        ...prev,
        ...newLookupEntries
      }));
    }

    if (missingIds.length > 0 && apiClient) {
      try {
        const response = await apiClient.post<{
          spheres: Record<string, BasicSphereInfo>;
          requestedCount: number;
          foundCount: number;
        }>('/spheres/batch-info', { ids: missingIds });

        setSphereLookup(prev => ({
          ...prev,
          ...response.spheres
        }));
      } catch {
        // Intentionally swallowed: assignee info is non-critical and failures should not block rendering
      }
    }
  }, [apiClient, sphereLookup, spheresByColumn]);

  /**
   * Clear all data
   */
  const clearData = useCallback(() => {
    setSpheresByColumn({});
    setPaginationState({});
    setSphereLookup({});
    setHasLoaded(false);
    setError(null);
    inflightRequests.current.clear();
  }, []);

  /**
   * Auto-load assignee info when spheres change
   */
  const loadAssigneeInfoInFlightRef = useRef(false);
  const lastLoadedSphereCountRef = useRef(0);

  useEffect(() => {
    const totalSphereCount = Object.values(spheresByColumn).reduce((sum, spheres) => sum + spheres.length, 0);

    if (totalSphereCount === lastLoadedSphereCountRef.current && totalSphereCount > 0) {
      return;
    }

    if (loadAssigneeInfoInFlightRef.current) {
      return;
    }

    const perspectiveIds: string[] = [];
    const auditIds: string[] = [];

    for (const spheres of Object.values(spheresByColumn)) {
      for (const sphere of spheres) {
        const perspectives = sphere.perspectives;
        if (perspectives && typeof perspectives === 'object') {
          for (const ids of Object.values(perspectives)) {
            if (Array.isArray(ids)) {
              perspectiveIds.push(...ids.filter((id): id is string => typeof id === 'string'));
            }
          }
        }

        const createdBy = sphere.properties?.createdBy || sphere.properties?.created_by;
        const updatedBy = sphere.properties?.updatedBy || sphere.properties?.updated_by;

        if (createdBy && typeof createdBy === 'string') {
          auditIds.push(createdBy);
        }
        if (updatedBy && typeof updatedBy === 'string') {
          auditIds.push(updatedBy);
        }
      }
    }

    const idsToLookup = [...perspectiveIds, ...auditIds];

    if (idsToLookup.length > 0) {
      lastLoadedSphereCountRef.current = totalSphereCount;
      loadAssigneeInfoInFlightRef.current = true;
      loadAssigneeInfo(idsToLookup).finally(() => {
        loadAssigneeInfoInFlightRef.current = false;
      });
    } else {
      lastLoadedSphereCountRef.current = totalSphereCount;
    }
  }, [spheresByColumn]); // loadAssigneeInfo intentionally excluded to prevent infinite loops

  /**
   * Check if a sphere is visible in the TreeMap (exists in any column).
   */
  const isSphereVisible = useCallback(
    (sphereId: string): boolean => {
      return Object.values(spheresByColumn).some(
        (spheres) => spheres.some((s) => s.id === sphereId)
      );
    },
    [spheresByColumn]
  );

  /**
   * Shared polling hook for refreshing sphere data after mutations.
   */
  // A refused one-sphere re-read reads the board the normal way, so a sphere the person can no longer
  // see leaves it (spec D71); a transient failure keeps the sphere as it is.
  const handleSphereRefused = useCallback(() => {
    void loadInitialData();
  }, [loadInitialData]);

  const { pollForUpdates, refreshSpheres } = useSpherePolling({
    apiClient,
    onSphereUpdate: updateSphereInState,
    isNodeVisible: isSphereVisible,
    includeAssignedPerspectives: true,
    onSphereRefused: handleSphereRefused,
  });

  return {
    spheresByColumn,
    paginationState,
    sphereLookup,
    loading,
    hasLoaded,
    error,
    loadInitialData,
    reloadWithFilters,
    loadChildren,
    refreshLevel,
    updateSphereInState,
    addSphereToState,
    loadAssigneeInfo,
    clearData,
    pollForUpdates,
    refreshSpheres,
  };
}
